/**
 * AccountMatcher Component
 * Radio group for selecting an existing account or creating a new one
 *
 * WP-DOC-20: Enhanced to show all accounts when suggestions are empty
 */
import { cn } from '@/utils/cn';
import AccountOption from './AccountOption';
import { AccountSelector } from '../AccountSelector';
import type { AccountSuggestion } from '@/types/document';
import type { Account } from '@/types';

export interface AccountMatcherProps {
  suggestions: AccountSuggestion[];
  /** All existing accounts - shown as fallback when suggestions are empty */
  allAccounts?: Account[];
  selectedAccountId: string | null;
  isCreatingNew: boolean;
  onSelectAccount: (accountId: string) => void;
  onSelectCreateNew: () => void;
}

export function AccountMatcher({
  suggestions,
  allAccounts = [],
  selectedAccountId,
  isCreatingNew,
  onSelectAccount,
  onSelectCreateNew,
}: AccountMatcherProps) {
  const hasSuggestions = suggestions.length > 0;
  const hasAllAccounts = allAccounts.length > 0;

  return (
    <div className="space-y-2" role="radiogroup" aria-label="Account selection">
      {/* Suggested accounts */}
      {suggestions.map((suggestion) => (
        <AccountOption
          key={suggestion.accountId}
          id={suggestion.accountId}
          accountName={suggestion.accountName}
          accountMask={suggestion.accountMask}
          accountType={suggestion.accountType}
          confidence={suggestion.confidence}
          matchReasons={suggestion.matchReasons}
          selected={selectedAccountId === suggestion.accountId}
          onSelect={() => onSelectAccount(suggestion.accountId)}
        />
      ))}

      {/* WP-DOC-20: No suggestions - show all accounts as fallback or warning */}
      {!hasSuggestions && hasAllAccounts && (
        <div className="space-y-3">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">
                  No automatic matches found
                </p>
                <p className="mt-1 text-sm text-blue-700">
                  We couldn&apos;t automatically match this document to an account. You can search
                  and select from your existing accounts below, or create a new one.
                </p>
              </div>
            </div>
          </div>

          {/* Searchable account selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select an existing account:
            </label>
            <AccountSelector
              accounts={allAccounts}
              selectedAccountId={selectedAccountId}
              onSelect={onSelectAccount}
              placeholder="Search by name, institution, or account number..."
            />
          </div>
        </div>
      )}

      {/* No suggestions and no accounts to show */}
      {!hasSuggestions && !hasAllAccounts && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3">
            <WarningIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">No matching accounts found</p>
              <p className="mt-1 text-sm text-yellow-700">
                We couldn&apos;t find any existing accounts that match this document. Please create
                a new account below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create new account option */}
      <div
        role="radio"
        aria-checked={isCreatingNew}
        tabIndex={0}
        onClick={onSelectCreateNew}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectCreateNew();
          }
        }}
        className={cn(
          'flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors',
          isCreatingNew
            ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
            : 'border-gray-200 hover:bg-gray-50'
        )}
      >
        {/* Radio indicator */}
        <div
          className={cn(
            'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
            isCreatingNew ? 'border-primary-600' : 'border-gray-300'
          )}
        >
          {isCreatingNew && <div className="w-2.5 h-2.5 rounded-full bg-primary-600" />}
        </div>

        {/* Content */}
        <div className="flex items-center gap-2">
          <PlusIcon
            className={cn(
              'w-5 h-5',
              isCreatingNew ? 'text-primary-600' : 'text-gray-400'
            )}
          />
          <span
            className={cn(
              'font-medium',
              isCreatingNew ? 'text-primary-700' : 'text-gray-700'
            )}
          >
            Create new account
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Info icon component (WP-DOC-20)
 */
function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  );
}

/**
 * Warning icon component
 */
function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

/**
 * Plus icon component
 */
function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

export default AccountMatcher;
